import React, { forwardRef, useCallback } from 'react';
import { useDrop } from 'react-dnd';
import {
  TrashIcon,
  DocumentDuplicateIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  Cog6ToothIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import DynamicField from '../DynamicForm/DynamicField';

const FormFieldPreview = ({ field, isSelected, onSelect, onUpdate, onDelete, onDuplicate, onMoveUp, onMoveDown, canMoveUp, canMoveDown, readOnly }) => {
  const handleFieldClick = (e) => {
    e.stopPropagation();
    if (!readOnly) {
      onSelect(field.id);
    }
  };

  const handleLabelChange = (e) => {
    e.stopPropagation();
    if (!readOnly) {
      onUpdate(field.id, { label: e.target.value });
    }
  };

  const toggleRequired = (e) => {
    e.stopPropagation();
    if (!readOnly) {
      onUpdate(field.id, { required: !field.required });
    }
  };

  const toggleVisibility = (e) => {
    e.stopPropagation();
    if (!readOnly) {
      const currentVisible = field.settings?.visible !== false;
      onUpdate(field.id, { 
        settings: { 
          ...field.settings, 
          visible: !currentVisible 
        } 
      });
    }
  };

  const isVisible = field.settings?.visible !== false;

  return (
    <div
      onClick={handleFieldClick}
      className={`
        relative group border-2 rounded-lg p-4 cursor-pointer transition-all
        ${isSelected 
          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
        }
        ${!isVisible ? 'opacity-50' : ''}
        ${readOnly ? 'cursor-default' : ''}
      `}
    >
      {/* Field Actions */}
      {!readOnly && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center space-x-1 bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-1">
            {/* Visibility Toggle */}
            <button
              onClick={toggleVisibility}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title={isVisible ? 'Hide field' : 'Show field'}
            >
              {isVisible ? (
                <EyeIcon className="h-4 w-4" />
              ) : (
                <EyeSlashIcon className="h-4 w-4" />
              )}
            </button>

            {/* Move Up */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveUp(field.id);
              }}
              disabled={!canMoveUp}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Move up"
            >
              <ArrowUpIcon className="h-4 w-4" />
            </button>

            {/* Move Down */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown(field.id);
              }}
              disabled={!canMoveDown}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Move down"
            >
              <ArrowDownIcon className="h-4 w-4" />
            </button>

            {/* Duplicate */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(field.id);
              }}
              className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
              title="Duplicate field"
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
            </button>

            {/* Delete */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (confirm('Are you sure you want to delete this field?')) {
                  onDelete(field.id);
                }
              }}
              className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
              title="Delete field"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Field Content */}
      <div className="space-y-3">
        {/* Editable Label */}
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={field.label}
            onChange={handleLabelChange}
            onClick={(e) => e.stopPropagation()}
            readOnly={readOnly}
            className={`
              text-sm font-medium bg-transparent border-none outline-none 
              text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400
              ${readOnly ? 'cursor-default' : 'cursor-text hover:bg-gray-50 dark:hover:bg-gray-800 px-1 py-0.5 rounded'}
            `}
            placeholder="Field label"
          />
          
          {/* Required Toggle */}
          {!readOnly && (
            <button
              onClick={toggleRequired}
              className={`
                text-xs px-2 py-1 rounded-full transition-colors
                ${field.required 
                  ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }
              `}
              title="Toggle required"
            >
              {field.required ? 'Required' : 'Optional'}
            </button>
          )}
        </div>

        {/* Field Preview */}
        <div className="pointer-events-none">
          <DynamicField
            field={field}
            value=""
            onChange={() => {}}
            disabled={true}
          />
        </div>

        {/* Field Type Badge */}
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
            {field.type}
          </span>
          
          {/* Configuration Hint */}
          {isSelected && !readOnly && (
            <span className="text-xs text-purple-600 dark:text-purple-400 flex items-center">
              <Cog6ToothIcon className="h-3 w-3 mr-1" />
              Configure in right panel
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const FieldDropZone = ({ onDrop, children, className = "" }) => {
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: 'fieldType',
    drop: (item) => {
      onDrop(item.fieldType);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }));

  return (
    <div
      ref={drop}
      className={`
        ${className}
        ${isOver && canDrop ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-600' : ''}
        transition-colors duration-200
      `}
    >
      {children}
    </div>
  );
};

const FormCanvas = forwardRef(({
  fields = [],
  selectedField,
  onSelectField,
  onUpdateField,
  onDeleteField,
  onDuplicateField,
  onMoveField,
  onAddField,
  readOnly = false
}, ref) => {
  const handleCanvasClick = useCallback(() => {
    if (!readOnly) {
      onSelectField(null);
    }
  }, [onSelectField, readOnly]);

  const handleFieldDrop = useCallback((fieldType, position = null) => {
    if (!readOnly && onAddField) {
      onAddField(fieldType, position);
    }
  }, [onAddField, readOnly]);

  if (fields.length === 0) {
    return (
      <FieldDropZone
        onDrop={handleFieldDrop}
        className="min-h-64 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center"
      >
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M34 40h10v-4a6 6 0 00-10.712-3.714M34 40H14m20 0v-4a9.971 9.971 0 00-.712-3.714M14 40H4v-4a6 6 0 016-6c.343 0 .682.036 1.005.103m3.712 9.897A9.97 9.97 0 0114 40m6-32a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No fields yet
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {readOnly 
              ? 'This form has no fields configured.'
              : 'Drag field types from the left panel or click them to add fields to your form.'
            }
          </p>
        </div>
      </FieldDropZone>
    );
  }

  return (
    <div ref={ref} className="space-y-4" onClick={handleCanvasClick}>
      {fields.map((field, index) => (
        <div key={field.id}>
          {/* Drop zone before each field */}
          {!readOnly && (
            <FieldDropZone
              onDrop={(fieldType) => handleFieldDrop(fieldType, index)}
              className="h-2 -mb-2 border border-transparent border-dashed rounded"
            />
          )}
          
          <FormFieldPreview
            field={field}
            isSelected={selectedField === field.id}
            onSelect={onSelectField}
            onUpdate={onUpdateField}
            onDelete={onDeleteField}
            onDuplicate={onDuplicateField}
            onMoveUp={() => onMoveField(field.id, 'up')}
            onMoveDown={() => onMoveField(field.id, 'down')}
            canMoveUp={index > 0}
            canMoveDown={index < fields.length - 1}
            readOnly={readOnly}
          />
        </div>
      ))}

      {/* Drop zone at the end */}
      {!readOnly && (
        <FieldDropZone
          onDrop={(fieldType) => handleFieldDrop(fieldType, fields.length)}
          className="h-12 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center text-sm text-gray-500 dark:text-gray-400"
        >
          Drop field here or click field types to add
        </FieldDropZone>
      )}
    </div>
  );
});

FormCanvas.displayName = 'FormCanvas';

export default FormCanvas;